import { useCallback, useMemo, useState } from 'react';

export const TABLE_PAGE_SIZE = 50;

export type OpenXAgentTablePagination = {
  page: number;
  pageCount: number;
  pageSize: number;
  count: number;
  onPageChange: (page: number) => void;
};

export const clampPage = (page: number, pageCount: number) => Math.min(Math.max(1, page), pageCount);

export const usePagination = <T>(items: T[], pageSize = TABLE_PAGE_SIZE) => {
  const [page, setPageState] = useState(1);
  const [previousItems, setPreviousItems] = useState(items);
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));

  // reset pagination every render
  if (items !== previousItems) {
    setPreviousItems(items);
    setPageState(1);
  }

  const currentPage = clampPage(page, pageCount);

  const pageItems = useMemo(
    () => items.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [items, currentPage, pageSize]
  );

  const setPage = useCallback((nextPage: number) => setPageState(clampPage(nextPage, pageCount)), [pageCount]);

  const pagination: OpenXAgentTablePagination = useMemo(
    () => ({ page: currentPage, pageCount, pageSize, count: items.length, onPageChange: setPage }),
    [currentPage, pageCount, pageSize, items.length, setPage]
  );

  return { pageItems, pagination };
};
